/**
 * QueuedRidesPage · v2.7.65+E147
 * ═════════════════════════════════════════════════════════════════════
 * Driver's assigned-ride queue. Chronological (oldest first = "up next").
 * Each row exposes Start Trip / Complete controls that fire the backend
 * queued-trips lifecycle. On complete, a real `db.trips` row is minted
 * server-side, so the driver sees it in Trip History and the receipt
 * permalink resolves.
 */
import { useCallback, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, Phone, Play, Flag, Users, Clock, RotateCcw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtUsd } from "@/lib/format";
import { openNavigation } from "@/components/NavigateButton";
import TripNavButtons from "@/components/TripNavButtons";
import DispatchNotesCard from "@/components/driver/DispatchNotesCard";
import RiderFlagsBadge from "@/components/RiderFlagsBadge";


function QueueRow({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showAck, setShowAck] = useState(false);
  const pickup = row.pickup_address;
  const dropoff = row.dropoff_address || row.dropoff_label;
  const flags = row.rider_flags || [];
  // v2.8+E321 · A flagged trip must be acknowledged by the driver before it
  // can start. Once acked (persisted server-side) we never re-prompt.
  const needsAck = flags.length > 0 && !row.flags_acked_at;
  // Actual start request — assumes navigation was already opened synchronously
  // by the click handler (mobile popup-blockers eat window.open behind await).
  const startTripRequest = async () => {
    setBusy(true);
    try {
      await api.post(`/drivers/queued-trips/${row.id}/start`);
      toast.success("Trip started · navigating to pickup");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trip");
    } finally { setBusy(false); }
  };
  // Start button (sync gesture). Flagged + unacked → gate behind the modal.
  const start = () => {
    if (needsAck) { setShowAck(true); return; }
    if (pickup) openNavigation(pickup); else toast.error("No pickup address on this trip");
    startTripRequest();
  };
  // Modal confirm (sync gesture) — acknowledge, then start.
  const confirmAckAndStart = async () => {
    if (pickup) openNavigation(pickup); else toast.error("No pickup address on this trip");
    setShowAck(false);
    setBusy(true);
    try {
      await api.post(`/drivers/queued-trips/${row.id}/ack-flags`);
      await api.post(`/drivers/queued-trips/${row.id}/start`);
      toast.success("Trip started · navigating to pickup");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trip");
    } finally { setBusy(false); }
  };
  const complete = async () => {
    if (!window.confirm("Mark this ride as complete? It will move to Trip History.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/drivers/queued-trips/${row.id}/complete`);
      toast.success(`Trip complete · ${fmtUsd(data?.trip?.total || 0)}`);
      if (data?.trip_id && window.confirm("Open the printable receipt?")) {
        window.open(`/receipt/${data.trip_id}`, "_blank");
      }
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    } finally { setBusy(false); }
  };

  const started = row.state === "in_progress";
  return (
    <>
    <Card className={`p-4 space-y-2 ${row.rider_flags?.length ? "border-2 border-red-500" : started ? "border-emerald-400/60" : ""}`} data-testid={`queue-row-${row.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${
              started ? "bg-emerald-500/20 text-emerald-500 border-emerald-400/40"
                      : "bg-amber-500/20 text-amber-500 border-amber-400/40"
            }`}>
              {started ? "IN PROGRESS" : "QUEUED"}
            </span>
            <span className="text-[10px] text-zinc-500 font-mono flex items-center gap-1">
              <Clock className="h-3 w-3"/>{row.assigned_at ? new Date(row.assigned_at).toLocaleTimeString() : "—"}
            </span>
          </div>
          <div className="font-bold text-zinc-100 mt-1">{row.rider_name || "Rider"}</div>
          {row.rider_phone && <div className="text-xs text-zinc-500 flex items-center gap-1"><Phone className="h-3 w-3"/>{row.rider_phone}</div>}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Fare</div>
          <div className="text-2xl font-black text-amber-500" data-testid={`queue-row-${row.id}-total`}>{fmtUsd(row.total)}</div>
        </div>
      </div>
      {/* v2.8+E319 · Rider flags — prominent, tap to view the full list. */}
      <RiderFlagsBadge flags={row.rider_flags} testId={`queue-row-${row.id}-rider-flags`}/>
      {/* Address order is LOCKED pickup-first, drop-off-second with explicit
          labels so drivers never misread which address is which. */}
      <div className="space-y-1.5 text-sm">
        <div className="flex items-start gap-1.5" data-testid={`queue-row-${row.id}-pickup`}>
          <MapPin className="h-3.5 w-3.5 mt-0.5 text-emerald-400 flex-shrink-0"/>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Pickup</div>
            <div className="text-zinc-200">{row.pickup_address || "—"}</div>
          </div>
        </div>
        <div className="flex items-start gap-1.5" data-testid={`queue-row-${row.id}-dropoff`}>
          <Flag className="h-3.5 w-3.5 mt-0.5 text-amber-400 flex-shrink-0"/>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Drop-off</div>
            <div className="text-zinc-100">{row.dropoff_address || row.dropoff_label || "—"}</div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-zinc-500 flex-wrap">
        <span className="flex items-center gap-1"><Users className="h-3 w-3"/>{row.passengers}</span>
        {row.round_trip && <span>Round trip</span>}
        {row.extra_stops > 0 && <span>{row.extra_stops} extra stop{row.extra_stops === 1 ? "" : "s"}</span>}
        {row.payment_method && (
          <span className="uppercase font-bold text-zinc-400" data-testid={`queue-row-${row.id}-payment`}>
            {row.payment_method === "already_processed" ? "Pre-paid" : row.payment_method}
          </span>
        )}
        {row.payment_status === "paid" && (
          <span className="uppercase font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-400/40" data-testid={`queue-row-${row.id}-paid`}>
            PAID · do not collect
          </span>
        )}
        {row.notes && !row.dispatch_notes && <span className="italic text-zinc-400 truncate max-w-[16rem]">"{row.notes}"</span>}
      </div>
      {/* v2.8 · Round-trip return details — visible to the driver without any edit. */}
      {row.round_trip && (
        <div className="rounded-md border border-cyan-500/40 bg-cyan-500/10 px-2.5 py-1.5 text-xs" data-testid={`queue-row-${row.id}-return`}>
          <div className="text-[9px] uppercase tracking-widest font-bold text-cyan-300 mb-0.5">Round Trip · Return Leg</div>
          <div className="flex items-center gap-1 text-zinc-200">
            <RotateCcw className="h-3 w-3 text-cyan-300 flex-shrink-0"/>
            {(row.return_pickup || row.dropoff_address || "—")} → {(row.return_dropoff || row.pickup_address || "—")}
          </div>
          {row.return_pickup_at && (
            <div className="text-[10px] text-cyan-200/80 mt-0.5">Return pickup: {new Date(row.return_pickup_at).toLocaleString()}</div>
          )}
        </div>
      )}
      {/* v2.7.65+E150 · CCVAP voucher badge — surface the serial the
          dispatcher verified so the driver can eyeball the paper voucher
          on arrival. */}
      {row.voucher_code && (
        <div
          className="rounded-md border border-amber-500/50 bg-amber-500/10 px-2.5 py-1.5 flex items-center gap-2 text-xs"
          data-testid={`queue-row-${row.id}-voucher`}
        >
          <span className="text-[9px] uppercase tracking-widest font-bold text-amber-400">CCVAP</span>
          <span className="font-mono font-bold text-amber-300">{row.voucher_code}</span>
          <span className="text-zinc-400">— cross-verify with rider's paper voucher on arrival</span>
        </div>
      )}
      {/* v2.8+E315 · Dispatch trip notes (from the Book Now form) — tappable,
          expandable card. Renders only when a dispatch note is present. */}
      <DispatchNotesCard note={row.dispatch_notes} testId={`queue-row-${row.id}-dispatch-notes`} onFirstOpen={() => api.post(`/drivers/queued-trips/${row.id}/notes-viewed`).catch(() => {})} />
      <div className="pt-1 border-t border-zinc-800 space-y-2">
        {!started ? (
          <Button size="sm" onClick={start} disabled={busy} data-testid={`queue-row-${row.id}-start`} className={`w-full font-bold ${needsAck ? "bg-red-600 hover:bg-red-500 text-white" : "bg-amber-500 hover:bg-amber-400 text-black"}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : (needsAck ? <TriangleAlert className="h-3.5 w-3.5 mr-1"/> : <Play className="h-3.5 w-3.5 mr-1"/>)}
            {needsAck ? "Review Flags to Start" : "Start Trip · Navigate to Pickup"}
          </Button>
        ) : (
          <>
            {/* Step 2 · drop-off is offered only after the driver has started
                (i.e. is heading to / has reached the pickup). */}
            <TripNavButtons pickup={pickup} dropoff={dropoff} testidPrefix={`queue-row-${row.id}-nav`}/>
            <Button size="sm" onClick={complete} disabled={busy} data-testid={`queue-row-${row.id}-complete`} className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <Flag className="h-3.5 w-3.5 mr-1"/>}
              Complete
            </Button>
          </>
        )}
      </div>
    </Card>
    {/* v2.8+E321 · Flag acknowledgment gate — the driver must review the
        rider flags before a flagged trip can start. */}
    {showAck && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid={`flag-ack-modal-${row.id}`} role="dialog" aria-modal="true">
        <div className="w-full max-w-sm rounded-xl border-2 border-red-500 bg-zinc-950 p-5 shadow-2xl shadow-red-900/40">
          <div className="flex items-center gap-2 mb-2">
            <TriangleAlert className="h-6 w-6 text-red-400 animate-pulse flex-shrink-0"/>
            <div className="text-lg font-black text-red-300 leading-tight">Rider Flags — Acknowledge</div>
          </div>
          <p className="text-sm text-zinc-300 mb-3">
            This rider has {flags.length} safety flag{flags.length > 1 ? "s" : ""}. Review {flags.length > 1 ? "them" : "it"} before you start the trip.
          </p>
          <RiderFlagsBadge flags={flags} mode="list" testId={`flag-ack-${row.id}-flags`}/>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setShowAck(false)} disabled={busy} data-testid={`flag-ack-cancel-${row.id}`}>Cancel</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold" onClick={confirmAckAndStart} disabled={busy} data-testid={`flag-ack-confirm-${row.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "I Acknowledge · Start"}
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}


export default function QueuedRidesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/drivers/queued-trips");
      setRows(data.queue || []);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // v2.8.1 · Bug fix — keep the queue live so a ride assigned from the Book
    // Ride form (or Ride Coordinator) shows up and stays actionable WITHOUT a
    // manual refresh. Silent poll (no spinner flicker) + refresh on focus.
    const onAssign = () => load(true);
    window.addEventListener("rtg:new_trip_created", onAssign);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    const poll = setInterval(() => load(true), 8000);
    return () => {
      window.removeEventListener("rtg:new_trip_created", onAssign);
      window.removeEventListener("focus", onFocus);
      clearInterval(poll);
    };
  }, [load]);

  return (
    <Layout title="Queued Rides">
      <div className="max-w-2xl mx-auto space-y-3 pb-24">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-500 font-bold">Dispatch Queue</div>
            <h1 className="text-xl font-black">Queued Rides</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Assigned rides in chronological order — oldest first.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="queue-refresh"><RotateCcw className="h-3.5 w-3.5"/></Button>
        </div>
        {loading ? (
          <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/>Loading queue…</div>
        ) : rows.length === 0 ? (
          <Card className="p-6 text-center text-sm text-zinc-500" data-testid="queue-empty">
            No queued rides right now. When a coordinator assigns you a ride it lands here instantly.
          </Card>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => <QueueRow key={r.id} row={r} onChanged={load}/>)}
          </div>
        )}
      </div>
    </Layout>
  );
}
